import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { defineCommand } from '../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { AppEvents } from '../AppEvents';
import { WorkspaceModel } from '../dashboard/Models';
import { inputSubmitPopup } from '../popups/InputPopup';
import { asyncOptionsPopupWith } from '../popups/OptionsPopup';
import { PopupManager } from '../popups/PopupManager';
import { createFolderPopup } from './CreateFolderPopup';

export const folderCreate = defineCommand(
    'folderCreate',
    'Folder: Create...',
    async (r, [workspaceId, label]: [string?, string?]) => {

        if (!workspaceId) return false;

        if (!label) {
            const { data } = await r.call(PopupManager).invoke(createFolderPopup(workspaceId));
            return data || false;
        }

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        const folder = await api.exec(v2.folders.create({
            workspaceId,
            label,
        }));

        evts.push('folder:create', { folder });
        return folder.id;
    },
);

export const folderCreateInCurrentWorkspace = defineCommand(
    'folderCreateInCurrentWorkspace',
    'Folder: Create In Current Workspace...',
    async (r, [label]: [string?]) => {

        const workspace = r.call(WorkspaceModel, false);
        if (!workspace) return false;

        const dispatch = r.call(CommandDispatcher);
        return await dispatch(folderCreate, [workspace.id, label]);
    },
);

export const folderRename = defineCommand(
    'folderRename',
    'Folder: Rename',
    async (r, [folderId, label]: [string, string]) => {

        if (!folderId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);
        const dispatch = r.call(CommandDispatcher);
        const popups = r.call(PopupManager);

        if (label === null || label === undefined) {

            const { actioned } = await popups.invoke(inputSubmitPopup({
                title: 'Rename Folder',
                placeholder: 'None',
                copy: 'Enter a new folder name:',
                submit: async value => {
                    await dispatch(folderRename, [folderId, value]);
                },
            }));

            return actioned;
        }

        const folder = await api.exec(v2.folders.update({ folderId, label }));
        evts.push('folder:patch', { folder });

        return true;
    }
);

export const folderDelete = defineCommand(
    'folderDelete',
    'Folder: Delete',
    async (r, [folderId]: [string]) => {

        if (!folderId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);
        const popups = r.call(PopupManager);

        const execute = () => api.exec(v2.folders.de1ete({ folderId }));
        const confirmation = asyncOptionsPopupWith(
            'Delete Folder',
            'Please confirm you want to delete this folder?  ' +
            'Any boards in the folder will be automatically moved to the workspace root.',
            [
                ['Delete Folder', 'negative', execute]
            ]
        );

        const { actioned, data } = await popups.invoke(confirmation);
        if (!actioned || !data) return false;

        evts.push('folder:delete', { folderId });
        return true;
    }
);
